import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert, PanelLeftOpen, Pencil } from "lucide-react";
import { useSidebar } from "@/lib/sidebar";
import { ApiError, memoryApi, type MemoryEntryOut, type MemoryKind } from "@/lib/api";
import { useSession } from "@/lib/useAuth";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Checkbox, Select } from "@/components/ui/Toggle";
import { Alert } from "@/components/ui/Alert";
import { RuleMemoryEditor } from "@/components/RuleMemoryEditor";
import { cn } from "@/lib/cn";

const KIND_OPTIONS: { value: MemoryKind | ""; label: string }[] = [
  { value: "", label: "全部 kind" },
  { value: "business.field_mapping", label: "业务 · 字段映射" },
  { value: "business.term", label: "业务 · 术语" },
  { value: "user.role", label: "用户 · 角色" },
  { value: "user.preference", label: "用户 · 偏好" },
  { value: "user.rule_template", label: "用户 · 私有规则" },
  { value: "user.episodic_event", label: "用户 · 情景事件" },
  { value: "skill.rule_template", label: "技能 · 规则模板" },
];

function readOccurredAt(payload: Record<string, unknown>): string | null {
  const ts = payload.occurred_at;
  if (typeof ts !== "number") return null;
  return new Date(ts * 1000).toLocaleString("zh-CN");
}

function readTriggers(payload: Record<string, unknown>): string[] {
  const t = payload.trigger_phrases;
  return Array.isArray(t) ? t.map((x) => String(x)) : [];
}

function scopeOf(kind: string): "business" | "user" | "skill" | "unknown" {
  if (kind.startsWith("business.")) return "business";
  if (kind.startsWith("user.")) return "user";
  if (kind.startsWith("skill.")) return "skill";
  return "unknown";
}

function MemoryRow({
  entry,
  selected,
  onToggle,
  canSelect,
  canEdit,
  onEdit,
}: {
  entry: MemoryEntryOut;
  selected: boolean;
  onToggle: (id: string) => void;
  canSelect: boolean;
  canEdit: boolean;
  onEdit: (entry: MemoryEntryOut) => void;
}): JSX.Element {
  const scope = scopeOf(entry.kind);
  const triggers = readTriggers(entry.payload);
  return (
    <li
      className={cn(
        "flex items-start gap-3 border-b border-zinc-100 px-4 py-3 last:border-b-0",
        (entry.quarantined || entry.superseded_by) && "opacity-60",
      )}
    >
      <div className="pt-1">
        <Checkbox
          checked={selected}
          onChange={() => onToggle(entry.id)}
          disabled={!canSelect}
          aria-label={`选择 ${entry.id}`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs">
          <code className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-zinc-600">
            {entry.kind}
          </code>
          <span className="text-zinc-400">scope: {scope}</span>
          <span className="text-zinc-400">v{entry.version}</span>
          {entry.quarantined ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-700">
              已隔离
            </span>
          ) : null}
          {entry.superseded_by ? (
            <span
              className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700"
              title={`已被 ${entry.superseded_by} 取代, 不再参与召回`}
            >
              已取代
            </span>
          ) : null}
        </div>
        <p className="mt-1.5 text-sm text-zinc-900">{entry.natural_text}</p>
        {entry.rule_description ? (
          <pre className="mt-1.5 whitespace-pre-wrap break-words rounded-md border border-zinc-100 bg-zinc-50 px-2.5 py-2 text-[12px] leading-5 text-zinc-600">
            {entry.rule_description}
          </pre>
        ) : null}
        {triggers.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {triggers.slice(0, 6).map((t, i) => (
              <span
                key={i}
                className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-500"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
        <p className="mt-1.5 text-xs text-zinc-500">
          {readOccurredAt(entry.payload) ? (
            <>
              发生于 {readOccurredAt(entry.payload)}
              <span className="mx-1.5">·</span>
            </>
          ) : null}
          confidence {(entry.confidence * 100).toFixed(0)}%<span className="mx-1.5">·</span>
          命中 {entry.hit_count} 次<span className="mx-1.5">·</span>
          <code className="font-mono">{entry.id.slice(0, 12)}…</code>
          {entry.related_ids && entry.related_ids.length > 0 ? (
            <>
              <span className="mx-1.5">·</span>🔗 {entry.related_ids.length} 关联
            </>
          ) : null}
        </p>
      </div>
      {canEdit && !entry.quarantined && !entry.superseded_by ? (
        <button
          type="button"
          onClick={() => onEdit(entry)}
          className="mt-0.5 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800"
          title="编辑"
        >
          <Pencil className="h-3.5 w-3.5" />
          编辑
        </button>
      ) : null}
    </li>
  );
}

export function MemoryCenterPage(): JSX.Element {
  const session = useSession();
  const queryClient = useQueryClient();
  const sidebar = useSidebar();
  const [kind, setKind] = useState<MemoryKind | "">("");
  const [includeQuarantined, setIncludeQuarantined] = useState(false);
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInfo, setActionInfo] = useState<string | null>(null);
  const [editing, setEditing] = useState<MemoryEntryOut | null>(null);

  const queryKey = [
    "memory",
    "entries",
    session.user_id,
    kind,
    includeQuarantined,
    includeSuperseded,
  ] as const;
  const query = useQuery({
    queryKey,
    queryFn: () =>
      memoryApi.listEntries(session.token, {
        kinds: kind ? [kind] : undefined,
        includeQuarantined,
        includeSuperseded,
      }),
  });

  const isAdmin = session.role === "admin";

  const canQuarantine = (e: MemoryEntryOut): boolean => {
    if (e.quarantined || e.superseded_by) return false;
    const scope = scopeOf(e.kind);
    return scope === "user" ? true : isAdmin;
  };

  // 私有 (user) 域直改即时生效; 共享域非 admin 仍可编辑, 但提交到审核队列。
  const canEditDirectly = (e: MemoryEntryOut): boolean => scopeOf(e.kind) === "user" || isAdmin;

  const eligibleSelected = useMemo(() => {
    if (!query.data) return [];
    return query.data.entries.filter((e) => selected.has(e.id) && canQuarantine(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, selected, isAdmin]);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const quarantineMutation = useMutation({
    mutationFn: () =>
      memoryApi.quarantine(
        session.token,
        eligibleSelected.map((e) => e.id),
        reason.trim() || undefined,
      ),
    onSuccess: (res) => {
      setSelected(new Set());
      setReason("");
      setActionError(null);
      const okN = res.quarantined.length;
      const badN = res.rejected.length;
      setActionInfo(
        badN === 0
          ? `已隔离 ${okN} 条`
          : `已隔离 ${okN} 条; 其中 ${badN} 条被拒 (${res.rejected.map((r) => r.reason).join(", ")})`,
      );
      queryClient.invalidateQueries({ queryKey: ["memory"] });
    },
    onError: (err: unknown) => {
      setActionInfo(null);
      setActionError(err instanceof ApiError ? err.message : "隔离失败");
    },
  });

  const consolidateMutation = useMutation({
    mutationFn: () => memoryApi.consolidate(session.token, 90),
    onSuccess: (res) => {
      setActionError(null);
      setActionInfo(`已归档 ${res.archived.length} 条 90 天以上的记忆`);
      queryClient.invalidateQueries({ queryKey: ["memory"] });
    },
    onError: (err: unknown) => {
      setActionInfo(null);
      setActionError(err instanceof ApiError ? err.message : "整理失败");
    },
  });

  const entries = query.data?.entries ?? [];

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl space-y-5 px-6 py-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[13px] text-zinc-500">
              {sidebar.collapsed ? (
                <button
                  type="button"
                  onClick={sidebar.toggle}
                  className="-ml-2 rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
                  title="展开侧栏"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                </button>
              ) : null}
              <span>Liando</span>
              <span className="text-zinc-300">/</span>
              <span className="text-zinc-800">长记忆中心</span>
            </div>
            <p className="mt-1 text-[12px] text-zinc-400">
              列出当前账户可见的条目 (自己的 USER scope + 共享的 business/skill)。 USER scope
              你可以自行隔离, BUSINESS / SKILL 仅管理员可操作。
            </p>
          </div>
          {isAdmin ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => consolidateMutation.mutate()}
              disabled={consolidateMutation.isPending}
            >
              {consolidateMutation.isPending ? "整理中…" : "归档 90 天以上"}
            </Button>
          ) : null}
        </header>

        {actionError ? <Alert tone="error">{actionError}</Alert> : null}
        {actionInfo ? <Alert tone="success">{actionInfo}</Alert> : null}

        <Card>
          <CardHeader
            title="筛选"
            action={
              <div className="flex items-center gap-4">
                <Select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as MemoryKind | "")}
                  options={KIND_OPTIONS}
                />
                <Checkbox
                  checked={includeQuarantined}
                  onChange={(e) => setIncludeQuarantined(e.target.checked)}
                  label="显示已隔离"
                />
                <Checkbox
                  checked={includeSuperseded}
                  onChange={(e) => setIncludeSuperseded(e.target.checked)}
                  label="显示已取代"
                />
              </div>
            }
          />
          {query.isLoading ? (
            <CardBody>
              <p className="text-sm text-zinc-400">加载中…</p>
            </CardBody>
          ) : query.isError ? (
            <CardBody>
              <p className="text-sm text-red-600">记忆列表加载失败。</p>
            </CardBody>
          ) : entries.length === 0 ? (
            <CardBody>
              <p className="text-sm text-zinc-400">没有匹配的记忆条目。</p>
            </CardBody>
          ) : (
            <ul>
              {entries.map((e) => (
                <MemoryRow
                  key={e.id}
                  entry={e}
                  selected={selected.has(e.id)}
                  onToggle={toggle}
                  canSelect={canQuarantine(e)}
                  canEdit
                  onEdit={setEditing}
                />
              ))}
            </ul>
          )}
        </Card>

        {eligibleSelected.length > 0 ? (
          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-red-600" />
                  批量隔离 ({eligibleSelected.length} 条)
                </span>
              }
              description="隔离后该条目不再参与召回; 可在 “显示已隔离” 中查看。"
            />
            <CardBody className="space-y-3">
              <textarea
                className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/20"
                rows={2}
                placeholder="可选: 备注隔离原因, 会写进 audit log"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setSelected(new Set())}>
                  取消选择
                </Button>
                <Button
                  variant="danger"
                  onClick={() => quarantineMutation.mutate()}
                  disabled={quarantineMutation.isPending}
                >
                  {quarantineMutation.isPending ? "提交中…" : "确认隔离"}
                </Button>
              </div>
            </CardBody>
          </Card>
        ) : null}

        {editing ? (
          <RuleMemoryEditor
            entry={editing}
            token={session.token}
            canEditDirectly={canEditDirectly(editing)}
            onClose={() => setEditing(null)}
            onSaved={(res) => {
              setActionError(null);
              setActionInfo(
                res.status === "pending_review"
                  ? "修改已提交审核, 管理员批准后生效"
                  : "已更新这条记忆",
              );
              setEditing(null);
              queryClient.invalidateQueries({ queryKey: ["memory"] });
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
